import { useState, useEffect } from 'react';

import { useCaptureException } from '@zenbusiness/application-commons-logging';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Heading from '@zenbusiness/zui/Heading';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import {
  classes,
  LabelStyled,
  StatusIconStyled,
  VGSInputCSS,
  VGSInputStyled
} from '#client/components/VGSInput';
import { CARD_PIN_STATUS, CARD_STATUS } from '#client/constants/cards';
import { PIN_NOT_MATCH_MESSAGE } from '#client/constants/messages';
import { SETUP_DEBIT_CARD_PIN } from '#client/constants/nextUpCards';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useCardsContext from '#client/hooks/useCardsContext';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useNextUpCardsContext from '#client/hooks/useNextUpCardsContext';
import useToastContext from '#client/hooks/useToastContext';
import useVGSCollectUnit from '#client/hooks/useVGSCollectUnit';
import { isAllFieldsValid, fieldHasErrors } from '#client/utils/vgs';

import { Wrapper } from '../SetPinModal.styles';

import { PIN_CONFIRM_FIELD_NAME, PIN_FIELD_NAME } from '../constants/fields';
import { PIN_NOT_MATCH_VGS_MESSAGE } from '../constants/messages';

const validIcon = (
  <SharedAsset
    alt=""
    src="/icon/micro/check-circle-green.svg"
  />
);

export default function PinInputForm({ handleClose }) {
  const [form, setForm] = useState(null);
  const [formIsValid, setFormIsValid] = useState(false);
  const [pinIsNotEqual, setPinIsNotEqual] = useState(false);
  const [pinIsError, setPinIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { removeCardWithAnimation } = useNextUpCardsContext();
  const captureException = useCaptureException();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.CARDS_SENSITIVE_WRITE });
  const {
    physicalCard,
    refetchCardPinStatus,
    physicalCardPinStatus,
    loading: loadingCard,
    error
  } = useCardsContext();
  const confirmButtonId = useEnterSubmit();
  const { createSuccessToast, createCommonErrorToast } = useToastContext();
  const { vgsCollect } = useVGSCollectUnit();
  const physicalCardStatus = physicalCard?.status;
  const isInactivateCard = physicalCardStatus === CARD_STATUS.INACTIVE;

  const newPinError = pinIsNotEqual || pinIsError;

  useEffect(() => {
    const isCloseModal =
      error ||
      (!loadingCard && (isInactivateCard || physicalCardPinStatus === CARD_PIN_STATUS.SET));

    if (isCloseModal) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCard, isInactivateCard, physicalCardPinStatus]);

  const initForm = (vgsCollectForm) => {
    const tempForm = vgsCollectForm.init((state) => {
      const isNotEqual = !!(
        state[PIN_CONFIRM_FIELD_NAME]?.errorMessages?.length === 1 &&
        state[PIN_CONFIRM_FIELD_NAME]?.errorMessages?.includes(PIN_NOT_MATCH_VGS_MESSAGE)
      );
      const isValid = isAllFieldsValid(state);
      setPinIsNotEqual(isNotEqual);
      setFormIsValid(isValid);
      setPinIsError(fieldHasErrors(PIN_FIELD_NAME, state));
    });

    tempForm
      .field('#cc-pin', {
        type: 'password',
        name: PIN_FIELD_NAME,
        maxLength: 4,
        validations: ['required', '/^([0-9]{4})$/'],
        css: VGSInputCSS,
        classes,
        autoFocus: true,
        autoComplete: 'off',
        inputMode: 'numeric'
      })
      .replacePattern('/[^\\d]+/g');

    tempForm
      .field('#cc-pin-confirm', {
        type: 'password',
        name: PIN_CONFIRM_FIELD_NAME,
        maxLength: 4,
        validations: [
          'required',
          '/^([0-9]{4})$/',
          {
            type: 'compareValue',
            params: {
              field: PIN_FIELD_NAME,
              function: 'match'
            }
          }
        ],
        css: VGSInputCSS,
        classes,
        autoComplete: 'off',
        inputMode: 'numeric'
      })
      .replacePattern('/[^\\d]+/g');

    setForm(tempForm);
  };

  useEffect(() => {
    if (!loadingCard && physicalCardPinStatus && !form && vgsCollect) {
      initForm(vgsCollect);
    }
  }, [physicalCardPinStatus, form, loadingCard, vgsCollect]);

  const handleResponse = (status, data) => {
    const defaultErrorMessage = new Error(`Set PIN error status: ${status}`);
    const sentryErrorMessage = new Error(
      `Set PIN error status: ${status} and data: ${JSON.stringify(data)}`
    );
    switch (status) {
      case 200:
        refetchCardPinStatus().finally(() => {
          handleClose({ shouldAddDebitCardPin: false });
          removeCardWithAnimation(SETUP_DEBIT_CARD_PIN);
          createSuccessToast({
            title: 'Your PIN is set',
            description: 'Now you can use your card for ATM and debit transactions.'
          });
        });
        break;
      default:
        captureException(sentryErrorMessage, { level: 'warning' });
        // Error from vgs, don't need to pass error object
        createCommonErrorToast({ error: defaultErrorMessage });
        handleClose();
        break;
    }
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      handleClose();
      return;
    }

    form.submit(
      `/cards/${physicalCard?.id}/secure-data/pin`,
      {
        mapDotToObject: 'true',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Authorization: `Bearer ${customerToken}`
        }
      },
      (status, data) => {
        handleResponse(status, data);
        setIsSubmitting(false);
      }
    );
  };

  if (loadingCard || physicalCardPinStatus !== CARD_PIN_STATUS.NOTSET) {
    return (
      <DialogContent>
        <LinearProgress />
      </DialogContent>
    );
  }

  return (
    <>
      <DialogContent>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper spacing="32px">
              <Stack spacing="16px">
                <Heading
                  size="3xl"
                  weight="bold"
                >
                  Now let&apos;s create your PIN.
                </Heading>
                <Text
                  color="tertiary"
                  size="lg"
                >
                  Choose a unique 4-digit PIN to use for ATM withdrawals or debit transactions. Then
                  keep it somewhere safe that you&apos;ll remember.
                </Text>
              </Stack>
              <Stack spacing="24px">
                <Stack
                  position="relative"
                  spacing="4px"
                  width="343px"
                >
                  <VGSInputStyled
                    id="cc-pin"
                    isError={newPinError}
                  >
                    <LabelStyled isError={newPinError}>New PIN</LabelStyled>
                  </VGSInputStyled>
                  {formIsValid && <StatusIconStyled>{validIcon}</StatusIconStyled>}
                </Stack>
                <Stack
                  position="relative"
                  spacing="4px"
                  width="343px"
                >
                  <VGSInputStyled id="cc-pin-confirm">
                    <LabelStyled>Confirm PIN</LabelStyled>
                  </VGSInputStyled>
                  {formIsValid && <StatusIconStyled>{validIcon}</StatusIconStyled>}
                  {pinIsNotEqual && (
                    <Text
                      color="red"
                      size="md"
                    >
                      {PIN_NOT_MATCH_MESSAGE}
                    </Text>
                  )}
                </Stack>
              </Stack>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContent>
      <ActionButtonsGrid>
        <LoadingButton
          color="primary"
          disabled={!formIsValid || isSubmitting}
          fullWidth
          id={confirmButtonId}
          loading={isSubmitting}
          onClick={handleFormSubmit}
          size="large"
          variant="contained"
        >
          Confirm
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

PinInputForm.propTypes = {
  handleClose: PropTypes.func.isRequired
};
